import { useEffect, useRef, useState } from "react";

export type DevDebugLogLine = {
  at: string;
  source: string;
  message: string;
};

const MAX_LINES = 400;

export function useDevDebugLog(enabled: boolean) {
  const [lines, setLines] = useState<DevDebugLogLine[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  function appendLocal(source: string, message: string) {
    setLines((prev) => {
      const next = [
        ...prev,
        { at: new Date().toISOString(), source, message },
      ];
      return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
    });
  }

  function clear() {
    setLines([]);
  }

  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      abortRef.current = null;
      return;
    }

    const ac = new AbortController();
    abortRef.current = ac;
    let buffer = "";

    void (async () => {
      try {
        const res = await fetch("/api/debug/logs", {
          credentials: "include",
          signal: ac.signal,
        });
        if (!res.ok || !res.body) return;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() ?? "";
          for (const chunk of chunks) {
            const dataLine = chunk.split("\n").find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            const payload = JSON.parse(dataLine.slice(5).trim()) as DevDebugLogLine;
            if (!payload.at || !payload.source || !payload.message) continue;
            setLines((prev) => {
              const next = [...prev, payload];
              return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
            });
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
      }
    })();

    return () => {
      ac.abort();
    };
  }, [enabled]);

  return { lines, appendLocal, clear };
}
